import {
  ErrorComponent,
  Outlet,
  createRoute,
  redirect,
  retainSearchParams,
  useRouter,
  type ErrorComponentProps,
} from "@tanstack/react-router"
import { isAxiosError } from "axios"

import { RobinWorkspace } from "@/components/agentic-ats/robin/robin-workspace"
import { PageAction } from "@/components/graphite/page-header"
import { queryClient } from "@/lib/query-client"
import { validateAgentWorkspaceSearch } from "./agent-workspace-search"
import AuthenticatedRoute from "./AuthenticatedRoute"
import { organizationsQueryOptions, selectOrganization } from "./SelectOrganizationRoute"

class OrganizationsLoadError extends Error {}

/**
 * The org gate. Runs before any ATS page renders or fetches, so no request is
 * ever sent for an org the user doesn't belong to. The stored org is only
 * trusted when `GET /orgObs/list` (login-only, ignores the org header) returns
 * it; a stale value (an org the user left, or one from another environment)
 * sends them to the picker instead of into a wall of 403s. A single-org user is
 * selected automatically.
 */
async function requireOrganization({ location }: { location: { href: string } }) {
  let organizations
  try {
    organizations = await queryClient.ensureQueryData(organizationsQueryOptions)
  } catch (error) {
    // 403 here means the user has no memberships at all.
    if (isAxiosError(error) && error.response?.status === 403) {
      throw redirect({ to: "/no-access", replace: true })
    }
    throw new OrganizationsLoadError("Could not load organizations")
  }

  const ids = organizations.flatMap((org) => (org.id ? [org.id] : []))
  if (ids.length === 0) throw redirect({ to: "/no-access", replace: true })

  const stored = localStorage.getItem("currentOrganization")
  if (stored && ids.includes(stored)) return
  if (ids.length === 1) return selectOrganization(ids[0])

  localStorage.removeItem("currentOrganization")
  throw redirect({ to: "/select-organization", search: { redirect: location.href }, replace: true })
}

function AgentWorkspace() {
  return (
    <RobinWorkspace>
      <Outlet />
    </RobinWorkspace>
  )
}

/** Only the org-list failure gets its own screen; anything else keeps the default. */
function AgentWorkspaceError(props: ErrorComponentProps) {
  const router = useRouter()
  if (!(props.error instanceof OrganizationsLoadError)) return <ErrorComponent {...props} />

  return (
    <div className="flex h-screen items-center justify-center bg-background px-6 text-foreground">
      <div className="flex max-w-[360px] flex-col items-center gap-4 text-center">
        <div className="flex flex-col gap-1.5">
          <div className="text-[15px] font-semibold">We couldn't load your organizations</div>
          <div className="text-[13px] leading-[1.5] text-muted-foreground">
            Check your connection and try again.
          </div>
        </div>
        <PageAction tone="primary" onClick={() => void router.invalidate()}>
          Try again
        </PageAction>
      </div>
    </div>
  )
}

const AgentWorkspaceRoute = createRoute({
  getParentRoute: () => AuthenticatedRoute,
  id: "agent-workspace",
  beforeLoad: requireOrganization,
  component: AgentWorkspace,
  errorComponent: AgentWorkspaceError,
  validateSearch: validateAgentWorkspaceSearch,
  // This route is a pathless ancestor of every ATS route, so this middleware runs
  // on every navigation beneath it. Without it the ~15 object-form `search: {...}`
  // navigations across the app would replace the search object wholesale and drop
  // the open conversation.
  search: { middlewares: [retainSearchParams(["c"])] },
})

export default AgentWorkspaceRoute
